import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, X, Plus, Check } from 'lucide-react';
import type { ProductToken } from '@sleed/bizcommerce-api';
import { Input } from '@/components/ui/input';
import { productsApi } from '~/lib/api/client';

/** A product chosen for a wishlist template — just what we need to render + persist. */
export interface SelectedProduct {
	id: number;
	nm: string;
	cd?: string | null;
	img1?: string | null;
}

interface Props {
	selected: SelectedProduct[];
	onChange: (next: SelectedProduct[]) => void;
}

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

/**
 * Async product search + multi-select for wishlist templates. Mirrors the legacy admin
 * ProductSelect (isMulti) backed by the product lookup endpoint. Selected products render as
 * removable chips; the result list toggles membership.
 */
export function ProductMultiSelect({ selected, onChange }: Props) {
	const { t } = useTranslation();
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<ProductToken[]>([]);
	const [loading, setLoading] = useState(false);
	const requestSeq = useRef(0);

	useEffect(() => {
		const term = query.trim();
		if (!term) {
			setResults([]);
			setLoading(false);
			return;
		}
		let cancelled = false;
		const seq = ++requestSeq.current;
		setLoading(true);
		const handle = setTimeout(() => {
			productsApi
				.lookup({ filter: { term, isEditAction: true }, pagination: { page: 1, pageSize: PAGE_SIZE } })
				.then(res => {
					if (cancelled || seq !== requestSeq.current) return;
					setResults(res.data ?? []);
				})
				.catch(() => {
					if (!cancelled && seq === requestSeq.current) setResults([]);
				})
				.finally(() => {
					if (!cancelled && seq === requestSeq.current) setLoading(false);
				});
		}, DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [query]);

	const selectedIds = new Set(selected.map(p => p.id));
	const toggle = (p: ProductToken) => {
		const id = p.id;
		if (id == null) return;
		if (selectedIds.has(id)) onChange(selected.filter(s => s.id !== id));
		else onChange([...selected, { id, nm: p.nm ?? '', cd: p.cd, img1: p.img1 }]);
	};
	const removeOne = (id: number) => onChange(selected.filter(s => s.id !== id));

	return (
		<div className="flex flex-col gap-2">
			{selected.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{selected.map(p => (
						<span
							key={p.id}
							className="inline-flex items-center gap-1 rounded-full border border-border bg-elevated py-0.5 pr-1 pl-2.5 text-xs text-foreground">
							<span className="max-w-48 truncate" title={p.nm}>
								{p.nm || `#${p.id}`}
							</span>
							<button
								type="button"
								onClick={() => removeOne(p.id)}
								aria-label={t('common.delete')}
								className="rounded-full p-0.5 text-foreground-muted hover:bg-muted hover:text-foreground">
								<X size={12} />
							</button>
						</span>
					))}
				</div>
			)}
			<div className="relative">
				<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
				<Input
					value={query}
					onChange={e => setQuery(e.target.value)}
					placeholder={t('catalog.wishlistTemplates.searchProducts')}
					className="pl-8"
				/>
			</div>
			{query.trim() && (
				<div className="max-h-56 overflow-y-auto rounded-md border border-border">
					{loading ? (
						<div className="flex items-center justify-center gap-2 py-6 text-sm text-foreground-muted">
							<Loader2 size={16} className="animate-spin" />
							{t('common.search')}…
						</div>
					) : results.length === 0 ? (
						<div className="py-6 text-center text-sm text-foreground-subtle">{t('common.noResults')}</div>
					) : (
						<ul className="divide-y divide-border-subtle">
							{results.map(p => {
								const isSel = p.id != null && selectedIds.has(p.id);
								return (
									<li key={p.id}>
										<button
											type="button"
											onClick={() => toggle(p)}
											className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-elevated">
											<span className="flex h-9 w-7 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border bg-background">
												{p.img1 ? <img src={p.img1} alt="" className="h-full w-full object-contain" /> : null}
											</span>
											<div className="min-w-0 flex-1">
												<div className="truncate text-sm text-foreground">{p.nm}</div>
												{p.cd && <div className="truncate font-mono text-[11px] text-foreground-muted">{p.cd}</div>}
											</div>
											{isSel ? (
												<Check size={15} className="shrink-0 text-success-fg" />
											) : (
												<Plus size={15} className="shrink-0 text-foreground-muted" />
											)}
										</button>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			)}
		</div>
	);
}
